import React from 'react';
import {
  getClassNameFactory,
  FlexGrid,
  getRehydratableName,
  Typography,
  TextBoldness,
} from '@dcl/ui';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import './CardTile.css';
import { InteractiveCardsProps, SectionData } from './types';
import { EnhancedCard } from './EnhancedCard';
import ReactHtmlParser from 'react-html-parser';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';

// Generate unique IDs for elements without relying on array indices
let uniqueId = 0;
const generateUniqueId = (prefix = 'item') => `${prefix}-${uniqueId++}`;

export const CardTile: React.FC<InteractiveCardsProps> = (props) => {
  const { cardDetails: rowsData, heading, description } = props;
  if (!rowsData) {
    return <></>;
  }

  const getClassName = getClassNameFactory('CardTile');

  const sections: SectionData[] = Array.isArray(rowsData)
    ? rowsData
    : [rowsData];

  const transformCardData = (cardData) => ({
    title: cardData.cardTitle,
    description: cardData.cardDescription,
    icon: cardData.iconPath,
    dropDown: cardData.dropdown,
    dropdownLabel: cardData.dropdownLabel,
    dropdownButtonName: cardData.dropdownButtonName,
    linkDetails: cardData.linkDetails.map((link) => ({
      label: link.linkText,
      value: link.linkPath,
      newTab: link.newTab,
    })),
  });

  const getColumnSize = (cardCount) => {
    if (cardCount === 1) return 12;
    if (cardCount === 2) return 6;
    if (cardCount === 3) return 4;
    return 12;
  };

  return (
    <div
      className={getClassName()}
      data-rehydratable={getRehydratableName(CardTile.displayName)}
      {...propsToDataAttributes(props)}
    >
      <div className={getClassName({ descendantName: 'container' })}>
        {(heading || description) && (
          <div className={getClassName({ descendantName: 'sectionHeader' })}>
            {heading && (
              <Typography
                tag={TypographyTags.SPAN}
                boldness={TextBoldness.Bold}
                className={getClassName({ descendantName: 'sectionHeading' })}
              >
                {heading}
              </Typography>
            )}
            {description && (
              <Typography
                tag={TypographyTags.P}
                className={getClassName({
                  descendantName: 'sectionDescription',
                })}
              >
                {ReactHtmlParser(description || '')}
              </Typography>
            )}
          </div>
        )}

        {sections.map((section) => {
          const cardCount = Number(section.numberOfColumns);
          const colSize = getColumnSize(cardCount);
          const smColSize = cardCount === 1 ? 12 : 6;
          const sectionKey = generateUniqueId('section');

          return (
            <FlexGrid key={sectionKey} fullWidth>
              <FlexGrid.Row className={getClassName({ descendantName: 'row' })}>
                {section.contentForRow.map((cardData) => {
                  const cardKey = generateUniqueId('card');

                  return (
                    <FlexGrid.Col
                      key={cardKey}
                      xs={12}
                      sm={smColSize}
                      md={colSize}
                      lg={colSize}
                      className={getClassName({ descendantName: 'column' })}
                    >
                      <EnhancedCard data={transformCardData(cardData)} />
                    </FlexGrid.Col>
                  );
                })}
              </FlexGrid.Row>
            </FlexGrid>
          );
        })}
      </div>
    </div>
  );
};

CardTile.displayName = 'CardTile';
